import React, { forwardRef, useState } from 'react';

/**
 * Input Component - Atomic Level
 * 
 * A flexible, accessible input component with validation states,
 * icons, and various configurations.
 * 
 * Features:
 * - Multiple variants (default, error, success)
 * - Size variations
 * - Left and right icon support
 * - Password visibility toggle
 * - Disabled state
 * - Full width option
 * - Accessible (ARIA attributes)
 */

export type InputVariant = 'default' | 'error' | 'success';
export type InputSize = 'sm' | 'md' | 'lg';

const variantClasses: Record<InputVariant, string> = {
  default: 
    'border-[#CCCCCC] bg-white text-[#1A1A1A] placeholder:text-[#999999] focus:border-[#EAB308] focus:ring-[#EAB308]/20',
  error: 
    'border-red-500 bg-white text-[#1A1A1A] placeholder:text-[#999999] focus:border-red-500 focus:ring-red-500/20',
  success: 
    'border-green-500 bg-white text-[#1A1A1A] placeholder:text-[#999999] focus:border-green-500 focus:ring-green-500/20',
};

const sizeClasses: Record<InputSize, string> = {
  sm: 'text-sm py-2 px-3',
  md: 'text-base py-2.5 px-4',
  lg: 'text-lg py-3 px-5',
};

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /** Input variant */
  variant?: InputVariant;
  /** Input size */
  size?: InputSize;
  /** Icon to display on the left side */
  leftIcon?: React.ReactNode;
  /** Icon to display on the right side */
  rightIcon?: React.ReactNode;
  /** Error message to display */
  error?: string;
  /** Success message to display */
  success?: string;
  /** Helper text to display below input */
  helperText?: string;
  /** Whether to show character count */
  showCount?: boolean;
  /** Maximum character length */
  maxLength?: number;
  /** Container class name */
  containerClassName?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className = '',
      containerClassName = '',
      variant: variantProp,
      size = 'md',
      type = 'text',
      leftIcon,
      rightIcon,
      error,
      success,
      helperText,
      showCount = false,
      maxLength,
      disabled,
      value,
      ...props
    },
    ref
  ) => {
    const [showPassword, setShowPassword] = useState(false);
    const [inputValue, setInputValue] = useState(value || '');
    
    // Determine variant based on error/success props
    const variant = error ? 'error' : success ? 'success' : variantProp || 'default';
    
    // Handle password visibility toggle
    const isPassword = type === 'password';
    const inputType = isPassword && showPassword ? 'text' : type;
    
    // Character count
    const currentLength = String(inputValue).length;
    
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(e.target.value);
      props.onChange?.(e);
    };

    const baseClasses = 'w-full rounded-lg border transition-all duration-200 focus:outline-none focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-50';
    const paddingClasses = `${leftIcon ? 'pl-10' : ''} ${rightIcon || isPassword ? 'pr-10' : ''}`;
    const inputClasses = `${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${paddingClasses} ${className}`;

    return (
      <div className={`w-full ${containerClassName}`}>
        <div className="relative">
          {/* Left Icon */}
          {leftIcon && (
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
              {leftIcon}
            </div>
          )}
          
          {/* Input Field */}
          <input
            ref={ref}
            type={inputType}
            disabled={disabled}
            value={value}
            maxLength={maxLength}
            onChange={handleChange}
            className={inputClasses}
            aria-invalid={!!error}
            aria-describedby={
              error ? `${props.id}-error` : 
              success ? `${props.id}-success` : 
              helperText ? `${props.id}-helper` : 
              undefined
            }
            {...props}
          />
          
          {/* Right Icon or Password Toggle */}
          {(rightIcon || isPassword) && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              {isPassword ? (
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="text-gray-400 hover:text-gray-600 focus:outline-none transition-colors"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              ) : (
                <div className="text-gray-400">{rightIcon}</div>
              )}
            </div>
          )}
        </div>
        
        {/* Helper Text, Error, Success, or Character Count */}
        <div className="mt-1.5 flex justify-between items-start">
          <div className="flex-1">
            {error && (
              <p id={`${props.id}-error`} className="text-sm text-red-600 flex items-center gap-1">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                {error}
              </p>
            )}
            {success && !error && (
              <p id={`${props.id}-success`} className="text-sm text-green-600 flex items-center gap-1">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                {success}
              </p>
            )}
            {helperText && !error && !success && (
              <p id={`${props.id}-helper`} className="text-sm text-gray-500">
                {helperText}
              </p>
            )}
          </div>
          
          {showCount && maxLength && (
            <p className="text-sm text-gray-500 ml-2">
              {currentLength}/{maxLength}
            </p>
          )}
        </div>
      </div>
    );
  }
);

Input.displayName = 'Input';

export default Input;

